import { Link, useLocation } from "wouter";
import { Menu } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePrelaunch } from "@/hooks/use-prelaunch";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const navLinks = [
  { label: "Tools", href: "/docs" },
  { label: "Pricing", href: "/pricing" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "Docs", href: "/docs" },
];

export function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { user, isAuthenticated, isLoading } = useAuth();
  const prelaunch = usePrelaunch();
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition duration-300 ${
        scrolled
          ? "bg-navy-900/95 border-b border-navy-700"
          : "bg-navy-900/80 border-b border-transparent"
      }`}
      data-testid="nav-main"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/">
            <span
              className="text-xl font-bold text-white cursor-pointer select-none"
              data-testid="link-home"
            >
              PropertyMCP
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link key={link.label} href={link.href}>
                <span
                  className={`text-sm font-medium transition-colors cursor-pointer ${
                    location === link.href
                      ? "text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                  data-testid={`link-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </span>
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {!isLoading && (
              <>
                {isAuthenticated ? (
                  <Link
                    href={user?.isAdmin ? "/admin" : "/dashboard"}
                    data-testid="link-dashboard"
                  >
                    <span className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2 font-semibold text-sm transition-colors cursor-pointer">
                      Dashboard
                    </span>
                  </Link>
                ) : prelaunch ? (
                  <Link href="/waitlist" data-testid="link-join-waitlist">
                    <span className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2 font-semibold text-sm transition-colors cursor-pointer">
                      Join Waitlist
                    </span>
                  </Link>
                ) : (
                  <>
                    <Link href="/login" data-testid="link-signin">
                      <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                        Sign In
                      </span>
                    </Link>
                    <Link href="/signup" data-testid="link-start-trial">
                      <span className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2 font-semibold text-sm transition-colors cursor-pointer">
                        Start Free Trial
                      </span>
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          <div className="md:hidden">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  data-testid="button-mobile-menu"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-72 bg-navy-900 border-navy-700 p-0"
              >
                <div className="flex flex-col h-full pt-12 px-6 pb-8">
                  <div className="flex flex-col gap-1">
                    {navLinks.map((link) => (
                      <SheetClose asChild key={link.label}>
                        <Link href={link.href}>
                          <span
                            className={`block py-3 text-base font-medium transition-colors cursor-pointer ${
                              location === link.href
                                ? "text-white"
                                : "text-gray-400 hover:text-white"
                            }`}
                            data-testid={`link-mobile-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {link.label}
                          </span>
                        </Link>
                      </SheetClose>
                    ))}
                  </div>

                  <div className="mt-auto flex flex-col gap-3">
                    {!isLoading && (
                      <>
                        {isAuthenticated ? (
                          <SheetClose asChild>
                            <Link
                              href={user?.isAdmin ? "/admin" : "/dashboard"}
                              data-testid="link-dashboard-mobile"
                            >
                              <span className="block w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-semibold text-sm transition-colors cursor-pointer">
                                Dashboard
                              </span>
                            </Link>
                          </SheetClose>
                        ) : prelaunch ? (
                          <SheetClose asChild>
                            <Link href="/waitlist" data-testid="link-join-waitlist-mobile">
                              <span className="block w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-semibold text-sm transition-colors cursor-pointer">
                                Join Waitlist
                              </span>
                            </Link>
                          </SheetClose>
                        ) : (
                          <>
                            <SheetClose asChild>
                              <Link href="/login" data-testid="link-signin-mobile">
                                <span className="block w-full text-center text-gray-400 hover:text-white py-2.5 text-sm transition-colors cursor-pointer">
                                  Sign In
                                </span>
                              </Link>
                            </SheetClose>
                            <SheetClose asChild>
                              <Link href="/signup" data-testid="link-start-trial-mobile">
                                <span className="block w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-semibold text-sm transition-colors cursor-pointer">
                                  Start Free Trial
                                </span>
                              </Link>
                            </SheetClose>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
